<script setup lang="ts">
/**
 * How a scene ends: the quick replies it offers the pharmacist, or the echo
 * of the one they answered with.
 *
 * Every scene of the course ends this way, so the pair is held here rather
 * than written out in each one. The actions are served with their labels,
 * and a label doubles as its echo — so a reply reads as what the pharmacist
 * said. The echo takes the place of the replies: once the click is made,
 * there is nothing left to answer.
 */
import MedicationCheckupUserEcho from "@/medication-checkup/components/MedicationCheckupUserEcho.vue";
import MedicationCheckupQuickReply from "@/medication-checkup/components/MedicationCheckupQuickReply.vue";
import {MedicationCheckupAction} from "@/medication-checkup/interfaces";

const props = defineProps<{
    /**
     * The action clicked, while its submission is in flight.
     */
    clicked: MedicationCheckupAction | null;
    actions: MedicationCheckupAction[];
    /**
     * Whether the scene has reached its replies: they land with the card, or
     * at the end of a reveal.
     */
    shown?: boolean;
    /**
     * Whether the replies are held — a scene waiting on what the pharmacist
     * must fill in first, or on a draft still being written.
     */
    disabled?: boolean;
    /**
     * Whether the replies wait on answers the pharmacist has not given: they
     * look held, but a click reports the block instead of acting, for the
     * scene to point at what is missing.
     */
    blocked?: boolean;
    /**
     * What the scene says is missing, beside the replies.
     */
    warning?: string | null;
    /**
     * What the echo reads when no click is held: the action a record already
     * closed on.
     */
    echo?: string | null;
}>();

const emit = defineEmits<{act: [action: MedicationCheckupAction]; blocked: []}>();

const reply = (action: MedicationCheckupAction) => {
    if (props.blocked === true) {
        emit('blocked');

        return;
    }

    emit('act', action);
};
</script>

<template>
  <MedicationCheckupUserEcho
    v-if="clicked || echo"
    :text="clicked ? clicked.label : echo ?? ''"
    class="animate-message-in"
  />

  <div
    v-else-if="shown && actions.length > 0"
    class="flex flex-wrap items-center justify-end gap-2 animate-message-in"
  >
    <span
      v-if="warning"
      class="mr-auto ml-10 text-xs font-semibold text-red-light animate-message-in"
    >
      {{ warning }}
    </span>

    <MedicationCheckupQuickReply
      v-for="action in actions"
      :key="action.key"
      :label="action.label"
      :disabled="disabled"
      :class="{'opacity-50': blocked}"
      @click="reply(action)"
    />
  </div>
</template>
